import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Badge,
  Alert,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import {
  stripeConnectFixture,
  createConnectedAccountFixture,
  refreshStripeConnectFixture,
  dashboardLinkFixture,
  updateStripeConnectFixture,
} from './fixture';

export function StripeConnectPage() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  const canWrite = useHasPermission({ perms: ['stripeConnect.write'] });

  const query = useQuery({
    queryKey: ['stripeConnect', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return stripeConnectFixture();
      }
      return fetch('/v1/admin/stripe-connect');
    },
  });

  const createAccount = useApiMutation({
    live: (fetch) => fetch('/v1/admin/stripe-connect/create', { method: 'POST' }),
    fixture: () => createConnectedAccountFixture(),
    invalidate: ['stripeConnect'],
    successMessage: 'Connected Account angelegt — Onboarding-Link öffnet sich.',
  });

  const refresh = useApiMutation({
    live: (fetch) => fetch('/v1/admin/stripe-connect/refresh', { method: 'POST' }),
    fixture: () => refreshStripeConnectFixture(),
    invalidate: ['stripeConnect'],
    successMessage: 'Status mit Stripe synchronisiert.',
  });

  const updateFee = useApiMutation({
    live: (fetch, { applicationFeeBps }) =>
      fetch('/v1/admin/stripe-connect', {
        method: 'PUT',
        body: { applicationFeeBps },
      }),
    fixture: ({ applicationFeeBps }) => updateStripeConnectFixture({ applicationFeeBps }),
    invalidate: ['stripeConnect'],
    successMessage: (res) => `Application-Fee gesetzt auf ${res.applicationFeeBps} bps.`,
  });

  if (query.isLoading) {
    return (
      <div>
        <PageHeader title="Stripe-Connect" />
        <QueryLoadingState />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div>
        <PageHeader title="Stripe-Connect" />
        <QueryErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const data = query.data;
  const ready = data.chargesEnabled && data.payoutsEnabled && data.detailsSubmitted;

  return (
    <div>
      <PageHeader
        title="Stripe-Connect"
        subtitle={`Connected-Account für diese Brand · GET /v1/admin/stripe-connect`}
        actions={
          data.accountId ? (
            <HasPermission perms={['stripeConnect.write']}>
              <Button color="primary" outline onClick={() => refresh.mutate()} disabled={refresh.isPending}>
                {refresh.isPending ? '↻…' : '↻ Status syncen'}
              </Button>
            </HasPermission>
          ) : null
        }
      />

      {!data.accountId ? (
        <NoAccountCard
          onCreate={() => createAccount.mutate()}
          busy={createAccount.isPending}
          canWrite={canWrite}
          onboardingUrl={createAccount.data?.onboardingUrl}
        />
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md="6">
              <StatusCard data={data} ready={ready} />
            </Col>
            <Col md="6">
              <RequirementsCard data={data} />
            </Col>
          </Row>

          <Row className="g-3">
            <Col md="6">
              <DashboardLinkCard />
            </Col>
            <Col md="6">
              <ApplicationFeeCard
                currentBps={data.applicationFeeBps}
                canWrite={canWrite}
                busy={updateFee.isPending}
                onSave={(bps) => updateFee.mutate({ applicationFeeBps: bps })}
              />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

function NoAccountCard({ onCreate, busy, canWrite, onboardingUrl }) {
  return (
    <Card>
      <CardBody className="text-center py-5">
        <div className="display-1 mb-3" aria-hidden>💳</div>
        <CardTitle tag="h4">Noch kein Connected-Account</CardTitle>
        <p className="text-muted">
          Stripe-Connect wickelt Payouts für diese Brand ab. Beim Erstellen wird
          ein Onboarding-Link generiert — der Brand-Inhaber durchläuft KYC bei
          Stripe.
        </p>
        {onboardingUrl ? (
          <Alert color="success" className="text-start mt-4">
            <strong>Account angelegt.</strong>
            <div className="mt-2 small">
              Onboarding-Link (24h gültig) — an Brand-Inhaber weitergeben:
            </div>
            <CopyChip value={onboardingUrl} label="Onboarding-Link" />
          </Alert>
        ) : (
          <HasPermission perms={['stripeConnect.write']}>
            <Button color="primary" onClick={onCreate} disabled={!canWrite || busy}>
              {busy ? 'Connected Account anlegen…' : 'Connected Account anlegen'}
            </Button>
          </HasPermission>
        )}
      </CardBody>
    </Card>
  );
}

function StatusCard({ data, ready }) {
  return (
    <Card className="h-100">
      <CardBody>
        <CardTitle tag="h5">Account-Status</CardTitle>
        <div className="small text-muted mb-3">
          <CopyChip value={data.accountId} label="Stripe-Account-ID" />
        </div>

        <div className="d-flex flex-column gap-2">
          <StatusRow ok={data.detailsSubmitted} label="Details eingereicht" />
          <StatusRow ok={data.chargesEnabled} label="Charges aktiviert" />
          <StatusRow ok={data.payoutsEnabled} label="Payouts aktiviert" />
        </div>

        {ready ? (
          <Alert color="success" className="small mt-3 mb-0">
            ✓ Vollständig live — Brand kann Zahlungen empfangen + auszahlen lassen.
          </Alert>
        ) : (
          <Alert color="warning" className="small mt-3 mb-0">
            KYC läuft noch. Status "↻ Status syncen" zieht aktuelle Werte von Stripe.
          </Alert>
        )}
      </CardBody>
    </Card>
  );
}

function StatusRow({ ok, label }) {
  return (
    <div className="d-flex justify-content-between align-items-center">
      <span className="small">{label}</span>
      <Badge color={ok ? 'success' : 'warning'} pill>
        {ok ? '✓ ok' : 'ausstehend'}
      </Badge>
    </div>
  );
}

function RequirementsCard({ data }) {
  const due = data.requirementsCurrentlyDue ?? [];
  const pending = data.pendingVerification ?? [];

  return (
    <Card className="h-100">
      <CardBody>
        <CardTitle tag="h5">Offene KYC-Anforderungen</CardTitle>
        {due.length === 0 && pending.length === 0 ? (
          <p className="text-success small mb-0">✓ Keine offenen Anforderungen.</p>
        ) : (
          <>
            {due.length > 0 && (
              <div className="mb-3">
                <div className="small text-muted text-uppercase mb-1">Sofort fällig</div>
                <div className="d-flex flex-wrap gap-1">
                  {due.map((r) => (
                    <Badge key={r} color="warning" pill className="orbit-code-badge-sm">
                      {r}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {pending.length > 0 && (
              <div>
                <div className="small text-muted text-uppercase mb-1">In Prüfung</div>
                <div className="d-flex flex-wrap gap-1">
                  {pending.map((r) => (
                    <Badge key={r} color="info" pill className="orbit-code-badge-sm">
                      {r}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {data.onboardingExpiresAt && due.length > 0 && (
          <Alert color="info" className="small mt-3 mb-0">
            Onboarding-Link läuft <RelativeTime iso={data.onboardingExpiresAt} /> ab.
          </Alert>
        )}
      </CardBody>
    </Card>
  );
}

function DashboardLinkCard() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const onGenerate = async () => {
    setBusy(true);
    try {
      const res = fixturesMode
        ? dashboardLinkFixture()
        : await fetch('/v1/admin/stripe-connect/dashboard-link', { method: 'POST' });
      setLink(res);
      // LOW-1 (security-review 2026-06-21): https:-Schema-Check vor open.
      if (typeof res?.url === 'string' && /^https:\/\//i.test(res.url)) {
        window.open(res.url, '_blank', 'noopener');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="h-100">
      <CardBody>
        <CardTitle tag="h5">Express-Dashboard</CardTitle>
        <p className="text-muted small">
          Generiert einen zeitlich begrenzten Login-Link (5 min) ins
          Stripe-Express-Dashboard für diesen Account.
        </p>
        <HasPermission perms={['stripeConnect.write']}>
          <Button color="primary" outline onClick={onGenerate} disabled={busy}>
            {busy ? '…' : '🔗 Express-Login generieren'}
          </Button>
        </HasPermission>
        {link && (
          <Alert color="info" className="small mt-3 mb-0">
            Tab wurde geöffnet. Link läuft <RelativeTime iso={link.expiresAt} /> ab.
          </Alert>
        )}
      </CardBody>
    </Card>
  );
}

function ApplicationFeeCard({ currentBps, canWrite, busy, onSave }) {
  const [bps, setBps] = useState(currentBps);
  const pct = (bps / 100).toFixed(2);
  const dirty = bps !== currentBps;

  return (
    <Card className="h-100">
      <CardBody>
        <CardTitle tag="h5">Application-Fee</CardTitle>
        <p className="text-muted small">
          Anteil der platform fee an jedem Charge. 100 bps = 1.00 %.
        </p>
        <div className="d-flex justify-content-between mb-2">
          <span className="display-6 fw-bold">{pct} %</span>
          <span className="text-muted">{bps} bps</span>
        </div>
        <Input
          type="range"
          min={0}
          max={500}
          step={10}
          value={bps}
          onChange={(e) => setBps(Number(e.target.value))}
          disabled={!canWrite || busy}
        />
        <div className="d-flex justify-content-between small text-muted">
          <span>0 %</span>
          <span>2.5 %</span>
          <span>5 %</span>
        </div>
        <FormGroup className="mt-2">
          <Label className="small">Direkter Wert (bps)</Label>
          <Input
            type="number"
            min={0}
            max={1000}
            step={10}
            value={bps}
            onChange={(e) => setBps(Math.max(0, Math.min(1000, Number(e.target.value) || 0)))}
            disabled={!canWrite || busy}
            bsSize="sm"
          />
        </FormGroup>
        <HasPermission perms={['stripeConnect.write']}>
          <Button color="primary" size="sm" disabled={!dirty || busy} onClick={() => onSave(bps)}>
            {busy ? 'Speichern…' : `Speichern${dirty ? ` (${bps - currentBps > 0 ? '+' : ''}${bps - currentBps} bps)` : ''}`}
          </Button>
        </HasPermission>
      </CardBody>
    </Card>
  );
}
